// Added by Sejune Oh on 2020-02-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { forwardRef, useState, useEffect } from 'react'
import { createStyles, Theme, makeStyles, useTheme } from '@material-ui/core'
import clsx from 'clsx'

// UI
import {
  OutlinedInput as MaterialOutlinedInput,
  OutlinedInputProps as MaterialOutlinedInputProps,
  FormHelperText as MaterialFormHelperText,
  FormControl as MaterialFormControl,
} from '@/components/_primes/v2'

export interface OutlinedInputProps extends MaterialOutlinedInputProps {
  /** Input label  */
  label?: string
  /** Label link */
  labelLink?: { link: string; text: string }
  /** 값이 들어올 수 있는 범위 가이드 */
  condition?: string
  /** 에러 메시지  */
  errMessage?: string
  /** 확인 메시지 */
  confirmMessage?: string
  /** 설명  */
  helperText?: string
  /** Full width box 여부  */
  fullwidth?: boolean
  /** Input 값을 검증하는 함수와 에러 메시지 */
  validators?: {
    /** Validate 함수 */
    validate: (value: any) => Promise<any>
    /** Validate 함수를 통과하지 못할 경우 노출되는 에러 메시지  */
    eMessage?: string
  }[]
  /** render를 강제로 발생시키고 싶은 경우 호출 */
  forceRender?: boolean
  children?: any
}

/**
 * Material UI의 OutlinedInput를 커스터마이징한 UI 입니다.
 */
const OutlinedInput = forwardRef(
  (
    {
      label,
      labelLink,
      onBlur,
      onFocus,
      validators,
      condition,
      fullwidth = false,
      disabled = false,
      helperText: argHelperText,
      errMessage: argErrMessage,
      confirmMessage: argConfirmMessage,
      inputProps,
      forceRender = false,
      ...props
    }: OutlinedInputProps,
    ref
  ) => {
    const theme = useTheme()
    const classes = useStyles(theme)

    const [focused, setFocused] = useState(false)
    const [helperText, setHelperText] = useState<string | null>(argHelperText || null)
    const [errorMessage, setErrorMessage] = useState<string | null>(argErrMessage || null)
    const [confirmMessage, setConfirmMessage] = useState<string | null>(argConfirmMessage || null)
    const [key, setKey] = useState<React.Key>()

    // # Blur evt handler
    const handleBlur = async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.persist()
      setFocused(false)

      if (validators) {
        const value = e.currentTarget.value
        let isErrOccured = false
        let validatorIndex = 0

        while (!isErrOccured && validators[validatorIndex]) {
          const { validate, eMessage } = validators[validatorIndex]

          try {
            await validate(value)
            validatorIndex++
          } catch (e) {
            setErrorMessage(eMessage || 'Invalid value...')
            isErrOccured = true
            break
          }
        }
      }

      if (onBlur) onBlur(e)
    }

    // ! Update error message from props
    // eslint-disable-next-line
    useEffect(() => setErrorMessage(argErrMessage || null), [argErrMessage])

    // ! Update helper text from props
    // eslint-disable-next-line
    useEffect(() => setHelperText(argHelperText || null), [argHelperText])

    // ! Update Confirm Message from props
    // eslint-disable-next-line
    useEffect(() => setConfirmMessage(argConfirmMessage || null), [argConfirmMessage])

    // ! Clear error message if disabled
    // eslint-disable-next-line
    useEffect(() => setErrorMessage(disabled ? null : argErrMessage || null), [disabled])

    // 내부 로직에 의해 defaultValue만 변경되면 render가 발생하지 않는다.
    // 이를 해결하기 위해 flag를 두어 reder를 강제로 발생시킨다.
    useEffect(() => {
      const default_value = props.defaultValue
      if (forceRender && (default_value || '' === default_value)) {
        setKey(`${label}_${default_value}`)
      }

      // eslint-disable-next-line
    }, [forceRender, props.defaultValue])

    // # Focus evt handler
    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(true)
      setErrorMessage(null)
      setHelperText(argHelperText || null)

      if (onFocus) onFocus(e)
    }

    const handleClickLabelLink = (link: string) => {
      // @ts-ignore
      const electron = window.clapp.getElectron()
      if (electron) {
        // @ts-ignore
        return electron.ipcRenderer.send('open-external-browser', { link })
      }

      return window.open(link)
    }

    return (
      <MaterialFormControl variant='outlined' className={classes.formControlWrapper}>
        {label && (
          <span
            className={classes.inputLabel}
            data-on-focused={focused}
            data-disabled={disabled}
            data-error={!!errorMessage}
          >
            {label}
            {labelLink && (
              <span className={classes.labelLink} onClick={() => handleClickLabelLink(labelLink.link)}>
                {labelLink.text}
              </span>
            )}
          </span>
        )}
        <MaterialOutlinedInput
          className={clsx(classes.inputWrapper, {
            [classes.inputNoDescWrapper]: !(condition || errorMessage || helperText),
          })}
          {...props}
          aria-describedby='cl-outer-lined-input'
          inputRef={ref}
          error={!!errorMessage}
          inputProps={{
            onBlur: handleBlur,
            onFocus: handleFocus,
            ...inputProps,
          }}
          data-fullwidth={fullwidth}
          disabled={disabled}
          key={key}
        />
        {condition && (
          <span
            className={classes.conditionText}
            data-has-helper={!!helperText}
            data-fullwidth={fullwidth}
            data-error={!!errorMessage}
          >
            {condition}
          </span>
        )}
        {(errorMessage || confirmMessage || helperText) && (
          <MaterialFormHelperText
            className={classes.formHelperText}
            id='filled-weight-helper-text'
            data-error={!!errorMessage}
            data-confirm={!!confirmMessage}
            data-cy-input-desc={`${props.id}Desc`}
            disabled={disabled}
          >
            {errorMessage || confirmMessage || helperText}
          </MaterialFormHelperText>
        )}
      </MaterialFormControl>
    )
  }
)

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControlWrapper: {
      position: 'relative',
      width: '100%',
      display: 'flex',
    },
    inputLabel: {
      transition: 'color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      color: '#fff',
      display: 'flex',
      justifyContent: 'space-between',
      flex: 1,
      marginBottom: '6px',
      '&[data-on-focused=true]': {
        color: '#145c82',
        fontWeight: 'bold',
      },
      '&[data-disabled=true]': {
        color: 'rgba(255, 255, 255, 0.23)',
      },
      '&[data-error=true]': {
        color: 'rgb(192, 70, 70)',
      },
    },
    labelLink: {
      color: '#007eff',
      cursor: 'pointer',
      verticalAlign: 'baseline',
    },
    inputWrapper: {
      color: '#fff',
      flex: 1,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fff',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#145c82',
      },
      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
        color: 'rgba(255, 255, 255, 0.23)',
      },
      '&[data-fullwidth=false]': {
        width: '50%',
      },
      '& > input': {
        padding: `${theme.spacing()}px`,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
      },
    },
    inputNoDescWrapper: {
      marginBottom: 20,
    },
    formHelperText: {
      flex: 1,
      color: 'rgba(255, 255, 255, 0.7)',
      margin: '0 3px',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&[data-error=true]': {
        color: 'rgb(192, 70, 70)',
      },
      '&[data-confirm=true]': {
        color: '#145c82',
      },
      '&.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.23)',
      },
    },
    conditionText: {
      position: 'absolute',
      right: '5px',
      bottom: '5px',
      color: 'rgba(255, 255, 255, 0.4)',
      '&[data-has-helper=true]': {
        bottom: 'calc(100% - 5em)',
      },
      '&[data-fullwidth=false]': {
        right: 'calc(50% + 5px)',
      },
      '&[data-error=true]': {
        color: 'rgb(192, 70, 70)',
      },
    },
  })
)

export default OutlinedInput
